/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VirtualServiceModule
 */

import {
    Component,
    Input,
    OnDestroy,
} from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { isEmpty, isUndefined } from 'underscore';
import { IVip } from 'generated-types';
import { IExtendedVipRuntimeDetail, OperStateType } from 'ajs/modules/vs/vs.types';

import { ConnectedPosition } from '@angular/cdk/overlay';
import * as globalL10n from 'global-l10n';
import './vs-address-grid-cell.component.less';

const { ...globalL10nKeys } = globalL10n;

const stateIconClassPrefix = 'vs-address-grid-cell__state-icon--';

/**
 * @description  Display the addresses list in the Virtual Service grid.
 * @author Dnyaneshwar Jadhav
 */
@Component({
    selector: 'vs-address-grid-cell',
    templateUrl: './vs-address-grid-cell.component.html',
})
export class VsAddressGridCellComponent implements OnDestroy {
    @Input()
    public vs: any;

    /**
     * currentOperStateType property holds current state of vip address in terms of up/down/pending
     * and currently it is used by vipListPopover component.
     */
    public currentOperStateType: OperStateType;

    /**
     * Property holding OperStateType.
     */
    public readonly operStateType = OperStateType;

    /**
     * List the VIPs with their addresses and states.
     */
    public vips: IExtendedVipRuntimeDetail[];

    public popoverControl$: Observable<boolean>;

    public readonly globalL10nKeys = globalL10nKeys;

    private readonly popoverControlSubject = new Subject<boolean>();

    public popoverPositions: ConnectedPosition[] = [
        {
            originX: 'center',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetY: -8,
            panelClass: 'right',
        },
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetY: -8,
            panelClass: 'right',
        },
        {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetY: -8,
            panelClass: 'left',
        },
    ];

    constructor() {
        this.popoverControl$ = this.popoverControlSubject.asObservable();
    }

    /**
     * Return the list of vip addresses.
     */
    private static vipSummaryReducer(acc: string[], vip: IVip): string[] {
        let addr = '';

        if (!isEmpty(vip.ip_address)) {
            addr = vip.ip_address.addr;

            if (!isEmpty(vip.floating_ip)) {
                addr += `(${vip.floating_ip.addr})`;
            }
        } else if (!isEmpty(vip.floating_ip)) {
            addr = vip.floating_ip.addr;
        }

        if (!isEmpty(vip.ip6_address)) {
            if (addr.length > 0) {
                addr += ', ';
            }

            addr += vip.ip6_address.addr;

            if (!isEmpty(vip.floating_ip6)) {
                addr += `(${vip.floating_ip6.addr})`;
            }
        } else if (!isEmpty(vip.floating_ip6)) {
            addr = vip.floating_ip6.addr;
        }

        acc.push(addr);

        return acc;
    }

    /**
     * Get the list of vipRuntime.
     */
    public get vipRuntimeList(): IExtendedVipRuntimeDetail[] {
        return this.vs.getRuntimeData()?.vip_summary as IExtendedVipRuntimeDetail[] ?? [];
    }

    /**
     * Close the existent vip summary popover.
     */
    public closeVipListPopover(): void {
        this.popoverControlSubject.next(false);
        this.vips = [];
        this.currentOperStateType = null;
    }

    /**
     * Return the runtime object with operStateType as 'Up'.
     */
    public get vipsWithUpState(): IExtendedVipRuntimeDetail[] {
        return this.getVipsByState(OperStateType.OPER_UP);
    }

    /**
     * Return the runtime object with operStateType as 'Down'.
     */
    public get vipsWithDownState(): IExtendedVipRuntimeDetail[] {
        return this.getVipsByState(OperStateType.OPER_DOWN);
    }

    /**
     * Return the runtime object with operStateType as 'Pending'.
     */
    public get vipsWithPendingState(): IExtendedVipRuntimeDetail[] {
        return this.getVipsByState(OperStateType.OPER_PENDING);
    }

    /**
     * Return a comma-separated string of VIP addresses.
     */
    public get vipListAsString(): string {
        const runtime = this.vs.getRuntimeData();

        if (isUndefined(runtime) || !Array.isArray(runtime.vip_summary)) {
            return '';
        }

        const vips = runtime.vip_summary
            .reduce(VsAddressGridCellComponent.vipSummaryReducer, []);

        return vips.join(', ');
    }

    /**
     * Return the class for the circle arrow icon based on the VIP state.
     */
    public getVipStateIconClass(operStateType: OperStateType): string {
        return stateIconClassPrefix + operStateType;
    }

    /**
     * Return the icon shape based on operStateType.
     */
    public getIconShape(operStateType: OperStateType): string {
        switch (operStateType) {
            case OperStateType.OPER_UP:
            case OperStateType.OPER_DOWN:
                return 'circle-arrow';

            case OperStateType.OPER_PENDING:
                return 'minus-circle';

            default:
                return '';
        }
    }

    /**
     * Return the icon direction based on operStateType.
     */
    public getIconDirection(operStateType: OperStateType): string {
        switch (operStateType) {
            case OperStateType.OPER_UP:
                return 'up';

            case OperStateType.OPER_DOWN:
                return 'down';

            default:
                return '';
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.closeVipListPopover();
    }

    /**
     * Open the popover with details such as VIPs and OperStateType ('Up', 'Down', 'Pending').
     */
    public openVipListPopover(operStateType: OperStateType, $event: MouseEvent): void {
        $event.stopPropagation();
        this.currentOperStateType = operStateType;
        this.vips = this.getVipsByState(operStateType);
    }

    /**
     * Return the runtime object based on OperStateType ('UP', 'DOWN', 'PENDING');
     */
    private getVipsByState(operStateType: OperStateType): IExtendedVipRuntimeDetail[] {
        return this.vipRuntimeList
            .filter((runtimeItem: IExtendedVipRuntimeDetail) => {
                return runtimeItem.operStateType === operStateType;
            });
    }

    public trackByIndex(index: number): number {
        return index;
    }
}
